import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { reducedMotion } from '../../hooks/useLenis.js';
import { useMotion } from '../../hooks/useMotion.js';
import './Introduction.css';

export const Introduction = () => {
  useMotion();

  const maskStageRef = useRef(null);
  const maskShapeRef = useRef(null);
  const maskFullLayerRef = useRef(null);
  const maskImgRef = useRef(null);
  const maskTextRef = useRef(null);

  const missionSectionRef = useRef(null);
  const missionLogoMaskRef = useRef(null);

  useEffect(() => {
    document.title = 'Introduction | ASTRID FIRST Wealth Advisory';
  }, []);

  useEffect(() => {
    if (reducedMotion()) return;

    const stage = maskStageRef.current;
    const maskShape = maskShapeRef.current;
    const fullLayer = maskFullLayerRef.current;
    const img = maskImgRef.current;
    const textBlock = maskTextRef.current;

    if (!stage || !maskShape) return;

    const mm = gsap.matchMedia();

    // Desktop and Tablet pinning, ultra-smooth mask zoom and text reveal
    mm.add('(min-width: 768px)', () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: stage,
          start: 'top top',
          end: '+=180%',
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        },
      });

      // 1. Eagle mask zooms outward with buttery smooth easing
      tl.fromTo(
        maskShape,
        {
          WebkitMaskSize: '220px auto',
          maskSize: '220px auto',
          opacity: 1,
        },
        {
          WebkitMaskSize: '7500px auto',
          maskSize: '7500px auto',
          duration: 1,
          ease: 'power2.inOut',
        },
        0
      );

      // 2. Subtle cinematic parallax depth on photo
      if (img) {
        tl.fromTo(
          img,
          { scale: 1.12 },
          { scale: 1, duration: 1, ease: 'none' },
          0
        );
      }

      // 3. Smooth crossfade to 100% unmasked full photo layer
      if (fullLayer) {
        tl.fromTo(
          fullLayer,
          { opacity: 0 },
          { opacity: 1, duration: 0.35, ease: 'power1.inOut' },
          0.5
        );
      }

      tl.to(
        maskShape,
        { opacity: 0, duration: 0.35, ease: 'power1.inOut' },
        0.5
      );

      // 4. Overlaid text reveals at bottom-left
      if (textBlock) {
        tl.fromTo(
          textBlock,
          { opacity: 0, y: 35 },
          { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' },
          0.65
        );
      }
    });

    // Mobile viewport (< 768px): smooth scrubbed mask zoom and text reveal
    mm.add('(max-width: 767px)', () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: stage,
          start: 'top 70%',
          end: 'bottom 20%',
          scrub: 1,
        },
      });

      tl.fromTo(
        maskShape,
        {
          WebkitMaskSize: '150px auto',
          maskSize: '150px auto',
          opacity: 1,
        },
        {
          WebkitMaskSize: '3600px auto',
          maskSize: '3600px auto',
          duration: 1,
          ease: 'power2.inOut',
        },
        0
      );

      if (fullLayer) {
        tl.fromTo(
          fullLayer,
          { opacity: 0 },
          { opacity: 1, duration: 0.35, ease: 'power1.inOut' },
          0.5
        );
      }

      tl.to(
        maskShape,
        { opacity: 0, duration: 0.35, ease: 'power1.inOut' },
        0.5
      );

      if (textBlock) {
        tl.fromTo(
          textBlock,
          { opacity: 0, y: 25 },
          { opacity: 1, y: 0, duration: 0.35, ease: 'power2.out' },
          0.6
        );
      }
    });

    return () => {
      mm.revert();
    };
  }, []);

  // GSAP animation for Mission & Vision section (matching How the relationship is set up in Services)
  useEffect(() => {
    if (reducedMotion()) return;

    const section = missionSectionRef.current;
    const mask = missionLogoMaskRef.current;
    if (!section || !mask) return;

    const mm = gsap.matchMedia();

    // Desktop and Tablet (>= 768px):
    mm.add('(min-width: 768px)', () => {
      gsap.fromTo(
        mask,
        {
          x: -25,
          opacity: 0.45,
        },
        {
          x: 35,
          opacity: 0.95,
          ease: 'power1.out',
          scrollTrigger: {
            trigger: section,
            start: 'top 80%',
            end: 'bottom 20%',
            scrub: 1.2,
          },
        }
      );
    });

    return () => {
      mm.revert();
    };
  }, []);

  return (
    <div className="introduction-page">
      <header className="page-head">
        <div className="page-head-inner">
          <p className="crumb">
            <a href="/">Home</a> / Introduction
          </p>
          <h1 data-reveal="chars">Introduction</h1>
          <p className="lead" data-reveal="fade">
            A MAS-licensed Independent Asset Manager and Multi Family Office
            platform, working for clients rather than for a product shelf.
          </p>
        </div>
      </header>

      <div className="spine">
        {/* Section 1: About Us */}
        <section className="section shell" id="intro-about">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">Since 2009</p>
              <h2 data-reveal="lines">About Us</h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                Established since 2009 and previously known as Goshen Global
                Management, ASTRID FIRST Wealth Advisory operates independently
                as a MAS-licensed Independent Asset Manager (IAM) and Multi
                Family Office (MFO) platform.
              </p>
              <p data-reveal="fade">
                We provide clients an alternative to traditional private
                banking. Your needs and objectives set the agenda, and we offer
                bespoke solutions, transparent pricing, versatile platforms and
                unbiased guidance for the benefit of your investment portfolio.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Picture Section: 100% Screen Size Image Mask Zoom Transition with Text Overlay */}
      <section
        className="section stage section--mask-transition"
        id="intro-team-mask"
        ref={maskStageRef}
      >
        <div className="mask-stage-pin">
          <div className="mask-viewport">
            {/* Full unmasked team photo (reveals 100% cleanly at scroll end) */}
            <div className="mask-full-layer" ref={maskFullLayerRef}>
              <img
                src="/media/homepage_ourpro.webp"
                alt="ASTRID FIRST Advisory Team"
                className="mask-team-img"
              />
            </div>

            {/* Single Eagle Logo Cutout Mask Layer */}
            <div className="mask-shape-layer" ref={maskShapeRef}>
              <img
                src="/media/homepage_ourpro.webp"
                alt="ASTRID FIRST Advisory Team"
                className="mask-team-img"
                ref={maskImgRef}
              />
            </div>

            {/* Luxury dark bottom gradient overlay for maximum legibility */}
            <div className="mask-photo-overlay" />

            {/* Overlaid Team Headline & Lead Copy */}
            <div className="mask-team-headline" ref={maskTextRef}>
              <p className="mask-headline-eyebrow">OUR PEOPLE</p>
              <h2 className="mask-headline-title">Our Team</h2>
              <p className="mask-headline-lead">
                Experienced leaders united by investment discipline, regional
                perspective, and a commitment to enduring value.
              </p>
              <div className="mask-headline-rule" />
            </div>
          </div>
        </div>
      </section>

      <div className="spine">
        {/* Section 2: Our Team ("The people" photo grid container deleted) */}
        <section className="section shell" id="intro-team">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">The people</p>
              <h2 data-reveal="lines">Our Team</h2>
            </div>
            <div className="prose">
              <p className="lead" data-reveal="fade">
                Our team brings together senior investment professionals, each
                with an average of more than 15 years of experience in wealth
                management, fund management and private banking.
              </p>
              <p data-reveal="fade">
                Our experts come from global financial institutions
                headquartered in the U.S., Europe (U.K. and Switzerland) and
                Asia, bringing a diverse perspective to the firm. That
                experience is what ensures you receive considered advice and
                steady support in managing your wealth.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Section 3: Mission & Vision — matching "How the relationship is set up" style */}
      <section
        ref={missionSectionRef}
        className="section mandate-section stage"
        id="intro-mission"
        aria-labelledby="mission-title"
      >
        <div className="mandate-bg-wrap" aria-hidden="true">
          <img
            src="/media/modern-architecture-and-cityscape-in-an-urban-envi-2026-09-15-20-02-22-utc.webp"
            alt="Modern Architecture and Corporate Heritage"
            className="mandate-bg-img"
          />
          <div className="mandate-overlay" />
          <div className="mandate-right-overlay" />
        </div>

        <div className="shell mandate-shell">
          <div className="mandate-grid">
            {/* Left side: Logo image mask with 100% height of this section */}
            <div className="mandate-visual-col" aria-hidden="true">
              <div className="mandate-logo-sticky">
                <div ref={missionLogoMaskRef} className="mandate-logo-mask" />
              </div>
            </div>

            {/* Right side: Title and content */}
            <div className="mandate-content-col">
              <div className="mandate-header">
                <p className="eyebrow mandate-eyebrow">What guides us</p>
                <h2
                  id="mission-title"
                  className="mandate-title"
                  data-reveal="lines"
                >
                  Mission & Vision
                </h2>
              </div>

              <div
                className="mission-lead"
                data-reveal="fade"
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontFamily: 'var(--sans)',
                  fontSize: '1.02rem',
                  lineHeight: 1.68,
                  marginBottom: 'clamp(1.4rem, 2.4vw, 2rem)',
                  maxWidth: '48ch',
                }}
              >
                To give families a way to manage significant wealth that is
                independent by structure, not merely by claim — and to remain
                the single point of contact as their circumstances change.
              </div>

              <div className="mandate-cards" data-reveal="group">
                <article className="mandate-card">
                  <div className="mandate-card-header">
                    <span className="mandate-card-badge">01</span>
                    <h3>Our Mission</h3>
                  </div>
                  <p>
                    To provide families and business owners with independent,
                    bespoke wealth management and conflict-free fiduciary
                    advisory, aligning every investment strategy with your
                    unique objectives and values.
                  </p>
                </article>

                <article className="mandate-card">
                  <div className="mandate-card-header">
                    <span className="mandate-card-badge">02</span>
                    <h3>Our Vision</h3>
                  </div>
                  <p>
                    To remain your trusted, single point of contact across
                    generations, delivering stability, institutional discipline,
                    and enduring wealth preservation as circumstances and global
                    markets evolve.
                  </p>
                </article>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="spine">
        {/* Section 4: Our Values */}
        <section className="section shell" id="intro-values">
          <div className="track">
            <div className="track-head track-head--sticky">
              <p className="eyebrow">How we behave</p>
              <h2 data-reveal="lines">Our Values</h2>
            </div>
            <div className="rows" data-reveal="group">
              <div className="row">
                <h3>Clients Focused</h3>
                <p>
                  We place our clients at the heart of everything we do. Our
                  client-centric approach ensures that your needs and goals are
                  our top priority, and we build long-term relationships based
                  on trust, understanding and mutual respect.
                </p>
              </div>
              <div className="row">
                <h3>Integrity</h3>
                <p>
                  We uphold the highest standards of integrity in all our
                  actions. Transparency, honesty and ethical behaviour are the
                  foundations of our business, and we are committed to doing
                  what is right for our clients, partners and team.
                </p>
              </div>
              <div className="row">
                <h3>Professionalism</h3>
                <p>
                  We maintain a high level of professionalism in every
                  interaction. Our team is dedicated to continuous learning, so
                  the advice we give stays informed and relevant, and we take
                  pride in delivering excellence in every part of our service.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};